import type { StructuredEvidenceValue } from '../../types/siteEvidence';
import { sourceSupportsFiberBuildDistanceFt } from './fiberBuildDistanceEvidence';
import { stripMissingDocumentValues } from './documentExtractSanity';
import { filterUtilityLetterFields } from './gridUtilityEvidence';
import { filterWaterStorageRequirementZeros } from './waterStorageEvidence';

/** Field-specific source checks shared by fresh extraction and cached Populate. */
export function filterRequirementFieldsBySource(
  requirementId: string,
  fields: Record<string, StructuredEvidenceValue>,
  body: string,
): void {
  stripMissingDocumentValues(fields);
  if (requirementId === 'municipal-utility-letter') filterWaterStorageRequirementZeros(fields, body);
  if (requirementId === 'utility-letter') filterUtilityLetterFields(fields, body);
  const clauses = body.split(/[\r\n]+|(?<=[.!?])\s+/);
  if (requirementId === 'aquifer-study') {
    // A parish-wide range or the maximum among thousands of wells is not
    // a yield measured at the project. Require the extracted number, test
    // context, and an individual/site well in the same source passage.
    const yieldValue = fields.estimatedYield_gpm;
    if (typeof yieldValue === 'number' && !clauses.some(line => {
      const normalized = line.replace(/(?<=\d),(?=\d{3}\b)/g, '');
      return /\b(?:pumping\s+test|pump\s+test|test(?:ed)?\s+(?:yield|rate)|sustainable\s+yield)\b/i.test(line)
        && /\b(?:well\s*(?:#|no\.?\s*)?\d+|(?:site|project|on[- ]site|subject)\s+well)\b/i.test(line)
        && new RegExp(`(?:^|[^\\d.])${String(yieldValue).replace('.', '\\.')}\\s*(?:gpm|gallons?\\s+per\\s+minute)\\b`, 'i').test(normalized)
        && !/\b(?:regional|parish[- ]wide|county[- ]wide|wells?\s+(?:ranged|range)|up\s+to|yield\s+range)\b/i.test(line);
    })) delete fields.estimatedYield_gpm;
    if (!clauses.some(line => /\b(?:static\s+water\s+level|depth\s+to\s+(?:ground)?water)\b/i.test(line)
      && /\b(?:ft|feet|foot)\b/i.test(line))) delete fields.staticWaterLevel_ft;
  }
  if (requirementId === 'btm-vendor-quote') {
    const offer = clauses.some(line => /\b(?:quot(?:e|ed|ation)|vendor\s+offer|budgetary\s+proposal)\b/i.test(line)
      && /\b(?:turbine|engine|genset|generator|generation\s+equipment|solar\s+(?:module|panel)|battery|BESS)\b/i.test(line)
      && !/\b(?:will\s+(?:obtain|request)|quotes?\s+(?:needed|required|pending)|seek(?:ing)?\s+quotes?)\b/i.test(line));
    if (!offer) { for (const key of Object.keys(fields)) delete fields[key]; return; }
  }
  if (requirementId === 'cultural') {
    const consultation = clauses.filter(line => /\b(?:SHPO|THPO|state\s+historic\s+preservation|tribal\s+(?:consultation|office)|section\s+106)\b/i.test(line));
    if (!consultation.length) { for (const key of Object.keys(fields)) delete fields[key]; return; }
    if (typeof fields.consultationDate === 'string' && !consultation.some(line =>
      /\b(?:consult(?:ed|ation)|meeting|response|letter|submitted)\b/i.test(line)
      && line.toLowerCase().includes(String(fields.consultationDate).toLowerCase()))) delete fields.consultationDate;
  }
  if (requirementId === 'mineral-title') {
    const prospective = /\b(?:will|shall|needed|required|pending|stage[ -]gated|may\s+be)\b/i;
    const titleClauses = clauses.filter(line => /\b(?:mineral\s+(?:title|ownership|waiver)|title\s+(?:report|opinion|examination|search|commitment))\b/i.test(line));
    const titleOpening = body.slice(0, 500);
    const actual = (/\b(?:mineral\s+title|title\s+(?:report|opinion|examination|search))\b/i.test(titleOpening) && !prospective.test(titleOpening))
      || /\b(?:purchase\s+option\s+agreement|conveyance\s+book|grantor|reserves?\s+(?:all\s+)?(?:oil|gas|mineral))\b/i.test(body);
    // An unrelated dated offer has neither a title report nor a prospective
    // title clause. Its author/date must not become verified mineral evidence.
    if (!actual && titleClauses.length === 0) {
      for (const key of Object.keys(fields)) delete fields[key];
      return;
    }
    if (!actual && titleClauses.some(line => prospective.test(line))) {
      delete fields.preparer; delete fields.reportDate;
      if (Array.isArray(fields.surfaceUseRestrictions)) {
        const actualRestrictions = fields.surfaceUseRestrictions.filter((value): value is string => typeof value === 'string' && !prospective.test(value));
        if (actualRestrictions.length) fields.surfaceUseRestrictions = actualRestrictions;
        else delete fields.surfaceUseRestrictions;
      }
    }
  }
  if (requirementId === 'fiber-survey') {
    // Proximity to a long-haul line or a unitless score is not a surveyed
    // construction route to a service handoff, and does not establish service.
    const distance = fields.buildDistance_ft;
    if (typeof distance === 'number' && !sourceSupportsFiberBuildDistanceFt(body, distance)) delete fields.buildDistance_ft;
    if (typeof fields.serviceType === 'string'
      && !body.toLowerCase().includes(fields.serviceType.toLowerCase())) delete fields.serviceType;
  }
  if (requirementId === 'survey') {
    const opening = body.slice(0, 12_000);
    if (/\bwetlands?\s+delineation\b/i.test(opening)
      && !/\b(?:topographic|boundary|land\s+title|ALTA(?:\s*\/\s*NSPS)?)\s+survey\b/i.test(opening)) {
      for (const key of Object.keys(fields)) delete fields[key];
    }
  }
  if (requirementId === 'parcel-survey') {
    if (typeof fields.parcelId === 'string' && /^(?:site|subject\s+(?:site|property)|property|tract|unknown|n\/?a)$/i.test(fields.parcelId.trim())) delete fields.parcelId;
    if (!clauses.some(line => /\b(?:easement|right[- ]of[- ]way|rights[- ]of[- ]way)\b/i.test(line)
      && !/\b(?:unverified|assumed|possible|potential|not\s+(?:shown|confirmed|established))\b/i.test(line))) delete fields.easements;
  }
  if (requirementId === 'species-survey') {
    // Ordinary wetland indicator vegetation is not a listed-species survey.
    if (Array.isArray(fields.speciesObserved)) {
      const listed = clauses.filter(line => /\b(?:threatened|endangered|federally\s+listed|state[- ]listed)\b/i.test(line));
      const species = fields.speciesObserved.filter((name): name is string => typeof name === 'string'
        && listed.some(line => line.toLowerCase().includes(name.toLowerCase())));
      if (species.length) fields.speciesObserved = species;
      else delete fields.speciesObserved;
    }
    if (!clauses.some(line => /\bcritical\s+habitat\b/i.test(line)
      && /\b(?:designated|designation|not\s+within|no\s+critical|outside|within\s+designated)\b/i.test(line)
      && !/\b(?:may|potential|unknown|undetermined)\b/i.test(line))) delete fields.criticalHabitat;
  }
  if (requirementId === 'community-feedback-summary') {
    if (/\bnational\s+opposition\s+context\b/i.test(body) && /\bfor\s+internal\s+use\b/i.test(body)) {
      for (const key of Object.keys(fields)) delete fields[key];
      return;
    }
    // A business introduction or utility discussion does not measure the public.
    const publicFeedback = /\b(?:public\s+(?:comment|hearing|feedback|meeting)|community\s+(?:feedback|survey|opposition|support|concerns)|stakeholder\s+(?:feedback|log|survey)|residents?\s+(?:oppos\w*|support\w*|concern\w*))\b/i.test(body);
    if (!publicFeedback) {
      for (const key of Object.keys(fields)) delete fields[key];
      return;
    }
    const score = fields.sentimentScore_0to100;
    if (typeof score === 'number' && !clauses.some(line => /\b(?:sentiment|support|approval)\s+(?:score|index|rating)\b/i.test(line)
      && new RegExp(`\\b${String(score).replace('.', '\\.')}\\b`).test(line))) delete fields.sentimentScore_0to100;
  }
}

export const REQUIREMENTS_WITH_SOURCE_FIELD_CHECKS = new Set([
  'municipal-utility-letter',
  'aquifer-study', 'fiber-survey', 'btm-vendor-quote', 'cultural', 'mineral-title', 'survey', 'parcel-survey', 'species-survey', 'community-feedback-summary',
]);
